import { useHistory, useLocation } from "react-router";
import { Header } from "src/pages/Welcome/components/Header";
import { Footer } from "src/pages/Welcome/components/Footer";
import { PrimaryButton } from "src/helpers/whiteLabel";
import { useAppContext } from "src/hooks/useAppContext";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { UUID } from "@screencloud/uuid";
import { Button, Notification } from "@screencloud/screencloud-ui-components";
import {
  ChannelLayout,
  CreateDemoChannelMutationVariables,
  GetFilesByChannelIdDocument,
  DemoChannelContentType,
} from "src/types.g";
import { getColors, getLogoSrc, queryBrandInfo } from "src/helpers/brandHelper";
import { FormattedMessage } from "react-intl";
import apolloClient from "src/state/apolloClient";
import { updateNewChannelToList } from "src/helpers/channelHelper";

import {
  DesignMyScreenContainer,
  DesignMyScreenModalContainer,
  TopSection,
  MiddleSection,
} from "./styles";
import { StepProgress } from "src/components/StepProgress";
import { ApolloProps, withData } from "./apollo";
import { TemplateSelection } from "./TemplateSelection";
import { Styling } from "./Styling";
import { LayoutSelection } from "./LayoutSelection";
import { ChannelPreview } from "./ChannelPreview";
import { PIXI_TRIALIST_PREFERENCES_KEYS } from "../constants";
import {
  StudioOnboardingFlow,
  trackMixpanelEvent,
  trackOnboardingStepEvent,
} from "src/utils/mixpanel";
import { CreateQuickPost } from "./CreateQuickPost";
import { TitleText } from "../components/TitleText";
import { useTemplateSelection } from "../hooks/useTemplateSelection";
import { useCreateQuickPost } from "../hooks/useCreateQuickPost";

export enum Step0ComponentEnum {
  TemplateSelection = "TemplateSelection",
  CreateQuickPost = "CreateQuickPost",
  UploadMedia = "UploadMedia",
}

type DesignYourContentProps = ApolloProps & {
  step0: {
    component: Step0ComponentEnum;
    displayTitleText: boolean;
    stepName?: string;
  };
};

const DesignYourContentComponent = ({
  loading,
  commonAppInstanceTemplates,
  createDemoChannel,
  updateOrgPixiTrialistData,
  step0,
}: DesignYourContentProps) => {
  const history = useHistory();
  const location = useLocation<{
    initialStep?: number;
    uploadedFileIds?: UUID[];
    step0Component: Step0ComponentEnum;
    onboardingFlow?: StudioOnboardingFlow;
  }>();
  const context = useAppContext();

  // Use step0Component from location state if provided, otherwise use prop
  const currentStep0 = location.state?.step0Component
    ? {
        component: location.state.step0Component,
        displayTitleText: true,
        stepName:
          location.state.step0Component === Step0ComponentEnum.UploadMedia
            ? "Upload"
            : step0.stepName,
      }
    : step0;

  const currentStep0Component = currentStep0?.component;
  const { stepName: step0Name = "Use case" } = currentStep0 || {};
  const steps = [step0Name, "Styling", "Layout"];
  const [currentStepIndex, setCurrentStepIndex] = useState(
    location.state?.initialStep || 0,
  );
  const [presetColors, setPresetColors] = useState({
    primary: "#000000",
    secondary: "#ffffff",
  });
  const [logoFileId, setLogoFileId] = useState<UUID | null>(null);
  const [logoThumbnail, setLogoThumbnail] = useState<string | null>(null);
  const [isFetchingBrandInfo, setIsFetchingBrandInfo] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const isOnboardingCompletionPending = useRef(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<ChannelLayout | null>(
    null,
  );
  const [demoChannelId, setDemoChannelId] = useState<string>();
  const [uploadedFileIds, setUploadedFileIds] = useState<UUID[]>([]);
  const lastStep = steps.length - 1;
  const isLastStep = currentStepIndex === lastStep;
  const {
    selectedTemplateIds,
    handleTemplateClick,
    getCreateDemoChannelParams: getTemplateSelectionCreateDemoChannelParams,
    disableNextButton: templateSelectionDisableNextButton,
  } = useTemplateSelection();
  const {
    title: quickPostTitle,
    body: quickPostBody,
    appPlatformData: quickPostAppPlatformData,
    uploadedFiles: quickPostUploadedFiles,
    disableNextButton: quickPostDisableNextButton,
    setTitle: setQuickPostTitle,
    setBody: setQuickPostBody,
    setUploadedFiles: setQuickPostUploadedFiles,
    setAppPlatformData: setQuickPostAppPlatformData,
    getCreateDemoChannelParams: getQuickPostCreateDemoChannelParams,
  } = useCreateQuickPost();

  const isQuickPostPage =
    currentStep0Component === Step0ComponentEnum.CreateQuickPost;
  const isCanvasPage =
    currentStep0Component === Step0ComponentEnum.TemplateSelection;
  const isUploadMediaPage =
    currentStep0Component === Step0ComponentEnum.UploadMedia;

  const step0DisableNextButton = useMemo(() => {
    if (isUploadMediaPage) {
      return uploadedFileIds.length === 0;
    }
    return templateSelectionDisableNextButton && quickPostDisableNextButton;
  }, [
    currentStep0Component,
    uploadedFileIds.length,
    templateSelectionDisableNextButton,
    quickPostDisableNextButton,
  ]);

  // Set uploaded file IDs from location state if they exist
  useEffect(() => {
    if (
      location.state?.uploadedFileIds &&
      location.state.uploadedFileIds.length > 0
    ) {
      setUploadedFileIds(location.state.uploadedFileIds);
    }
  }, [location.state?.uploadedFileIds]);

  useEffect(() => {
    trackMixpanelEvent({
      event: "Screen Design Initiated",
      context,
    });
  }, []);

  useEffect(() => {
    const fetchBrandInfo = async () => {
      const brandInfo = await queryBrandInfo(
        context.currentUser?.email?.split("@")[1] ?? "",
      );
      const { backgroundColor, textColor } = getColors(brandInfo);
      const logoSrc = getLogoSrc(brandInfo);
      setPresetColors({
        primary: backgroundColor ?? "#000000",
        secondary: textColor ?? "#ffffff",
      });
      setLogoThumbnail(logoSrc ?? null);
    };

    setIsFetchingBrandInfo(true);
    fetchBrandInfo();
    setIsFetchingBrandInfo(false);
  }, []);

  const handleGoBackToWelcome = useCallback(() => {
    history.push("/welcome/");
  }, [history]);

  const handleGoBackToCreateContentOptions = useCallback(() => {
    history.push("/welcome/create-content-options", {
      onboardingFlow: location.state?.onboardingFlow,
    });
  }, [history, location.state?.onboardingFlow]);

  const checkFileStatus = async (channelId: string): Promise<boolean> => {
    const { data } = await apolloClient.query({
      query: GetFilesByChannelIdDocument,
      variables: { id: channelId },
      fetchPolicy: "network-only",
    });

    const files = data.channelById?.filesByChannelId?.nodes || [];
    return files.every((file) => file.fileProcessingStatus !== null);
  };

  const poll = async (
    checkFn: () => Promise<boolean>,
    interval = 3000,
    timeout = 60000,
  ): Promise<boolean> => {
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      const result = await checkFn();
      if (result) return true;
      await new Promise((resolve) => setTimeout(resolve, interval));
    }

    return false;
  };

  const handleNext = async () => {
    if (isLastStep && selectedLayout) {
      setIsGenerating(true);

      // Determine content based on step0 component type
      let content;
      if (isUploadMediaPage) {
        content = {
          type: DemoChannelContentType.File,
          refIdData: uploadedFileIds,
        };
      } else if (isQuickPostPage) {
        content = getQuickPostCreateDemoChannelParams();
      } else {
        content = getTemplateSelectionCreateDemoChannelParams();
      }

      const createDemoChannelInput: CreateDemoChannelMutationVariables = {
        input: {
          spaceId: context.currentSpace?.id,
          commonAppInstanceTemplateIds:
            getTemplateSelectionCreateDemoChannelParams().refIdData, // TODO: Follow backend logic
          layout: selectedLayout,
          theme: {
            logoFileId,
            primaryColor: presetColors.primary,
            secondaryColor: presetColors.secondary,
          },
          content,
        },
      };
      try {
        const { data } = await createDemoChannel({
          variables: createDemoChannelInput,
          update: (cache, { data }) => {
            if (data?.createDemoChannel?.channel) {
              updateNewChannelToList(
                context,
                cache,
                data?.createDemoChannel?.channel,
              );
            }
          },
        });
        if (data?.createDemoChannel?.channel?.id) {
          // Start polling for file status
          await poll(() =>
            checkFileStatus(data.createDemoChannel?.channel?.id),
          );

          setIsGenerating(false);
          setIsGenerated(true);
          setDemoChannelId(data.createDemoChannel?.channel?.id);
          const isFirstCompletion =
            context.currentOrg?.preferences.pixi_trialist
              ?.completed_design_screen_content !== true;
          const onboardingFlow = location.state?.onboardingFlow;
          const shouldTrackOnboardingCompletion =
            isFirstCompletion &&
            Boolean(onboardingFlow) &&
            !isOnboardingCompletionPending.current;
          if (shouldTrackOnboardingCompletion) {
            isOnboardingCompletionPending.current = true;
          }
          const updatePreference = updateOrgPixiTrialistData({
            variables: {
              input: {
                key: PIXI_TRIALIST_PREFERENCES_KEYS.COMPLETED_DESIGN_SCREEN_CONTENT,
                value: true,
              },
            },
          });
          if (shouldTrackOnboardingCompletion && onboardingFlow) {
            updatePreference.then(
              () => {
                trackOnboardingStepEvent({
                  context,
                  status: "completed",
                  stepName: "design_screen_content",
                  stepNumber: 1,
                  onboardingFlow,
                });
              },
              () => {
                isOnboardingCompletionPending.current = false;
              },
            );
          }
          return;
        }
      } catch (error) {
        setIsGenerating(false);
        console.error(error);
        Notification.error({
          message: context.intl.formatMessage({
            defaultMessage: "Something went wrong! Please try again.",
            id: "design_my_screen_modal.error",
          }),
        });
        return;
      } finally {
        trackMixpanelEvent({
          event: "Screen Design Completed",
          context,
        });
      }
    }
    if (currentStepIndex === 0) {
      trackMixpanelEvent({
        event: "Screen Design Use Case Completed",
        context,
      });
    }
    if (currentStepIndex === 1) {
      trackMixpanelEvent({
        event: "Screen Design Styling Completed",
        context,
      });
    }
    setCurrentStepIndex((prevIndex) => prevIndex + 1);
  };

  const handlePrevious = () => {
    // Special case: if we're on step 1 and came from UploadMedia flow,
    // go back to create-content-options instead of step 0
    if (currentStepIndex === 1 && isUploadMediaPage) {
      history.push("/welcome/create-content-options", {
        onboardingFlow: location.state?.onboardingFlow,
      });
      return;
    }

    if (currentStepIndex === 0) {
      return;
    }
    setCurrentStepIndex((prevIndex) => prevIndex - 1);
  };

  const onPrimaryColorChange = (color: string) => {
    setPresetColors((prevColors) => ({ ...prevColors, primary: color }));
  };

  const onSecondaryColorChange = (color: string) => {
    setPresetColors((prevColors) => ({ ...prevColors, secondary: color }));
  };

  const handleUploadLogo = async () => {
    setIsUploadingLogo(true);
    const files = await context.modal
      .openMediaUpload(
        <FormattedMessage
          id="design_my_screen_modal.styling.upload_logo"
          defaultMessage="Upload Logo"
        />,
        {
          maxFiles: 1,
          folderId: null,
          onClose: () => {
            setIsUploadingLogo(false);
          },
          isGlobalUpload: true,
        },
      )
      .catch((e) => {
        setIsUploadingLogo(false);
        return Promise.reject([]);
      });

    if (files?.length > 0) {
      setLogoFileId(files[0].id);
      const thumbnail = files[0].source;
      setLogoThumbnail(thumbnail);
    }
    context.modal.closeUploadModals();
    setIsUploadingLogo(false);
  };

  const handleLayoutClick = (layout: ChannelLayout) => {
    setSelectedLayout(layout);
  };

  const getContent = (): JSX.Element => {
    switch (currentStepIndex) {
      case 0:
        if (isQuickPostPage) {
          return (
            <CreateQuickPost
              title={quickPostTitle}
              body={quickPostBody}
              setTitle={setQuickPostTitle}
              setBody={setQuickPostBody}
              uploadedFiles={quickPostUploadedFiles}
              setUploadedFiles={setQuickPostUploadedFiles}
              appPlatformData={quickPostAppPlatformData}
              setAppPlatformData={setQuickPostAppPlatformData}
            />
          );
        } else {
          return (
            <TemplateSelection
              loading={loading}
              commonAppInstanceTemplates={commonAppInstanceTemplates}
              selectedTemplateIds={selectedTemplateIds}
              handleTemplateClick={handleTemplateClick}
            />
          );
        }
      case 1:
        return (
          <Styling
            presetColors={presetColors}
            onPrimaryColorChange={onPrimaryColorChange}
            onSecondaryColorChange={onSecondaryColorChange}
            handleUploadLogo={handleUploadLogo}
            logoThumbnail={logoThumbnail}
            isUploadingLogo={isUploadingLogo}
            isFetchingBrandInfo={isFetchingBrandInfo}
          />
        );
      case 2:
        return (
          <LayoutSelection
            selectedTemplateIds={selectedTemplateIds}
            commonAppInstanceTemplates={commonAppInstanceTemplates}
            selectedLayout={selectedLayout}
            handleLayoutClick={handleLayoutClick}
            logoThumbnail={logoThumbnail}
            presetColors={presetColors}
            isGenerating={isGenerating}
            isCanvasPage={isCanvasPage}
          />
        );
      default:
        return <div></div>;
    }
  };

  if (isGenerated && demoChannelId) {
    return (
      <DesignMyScreenContainer>
        <Header
          title="Nice, your content looks great!"
          onBackButtonClick={handleGoBackToWelcome}
        />
        <ChannelPreview channelId={demoChannelId} />
        <Footer justifyContent="center">
          <PrimaryButton onClick={handleGoBackToWelcome}>
            Complete
          </PrimaryButton>
        </Footer>
      </DesignMyScreenContainer>
    );
  }

  return (
    <DesignMyScreenContainer>
      <Header
        title="Design your content"
        onBackButtonClick={handleGoBackToCreateContentOptions}
      />

      <DesignMyScreenModalContainer>
        <TopSection>
          <StepProgress currentStep={currentStepIndex + 1} totalSteps={steps} />
          <div>
            {isGenerating ? (
              <TitleText
                currentStepIndex={currentStepIndex}
                titleText={context.intl.formatMessage({
                  id: "design_my_screen_modal.title.generating",
                  defaultMessage: "Generating your screen content...",
                })}
                subtitleText={context.intl.formatMessage({
                  id: "design_my_screen_modal.subtitle.generating",
                  defaultMessage:
                    "We are generating the perfect content for your digital signage. You can come back and edit this at anytime.",
                })}
              />
            ) : (
              <TitleText
                currentStepIndex={currentStepIndex}
                displayTitleText={currentStep0?.displayTitleText ?? true}
              />
            )}
          </div>
        </TopSection>
        <MiddleSection>{getContent()}</MiddleSection>
      </DesignMyScreenModalContainer>
      <Footer
        justifyContent={currentStepIndex !== 0 ? "space-between" : "flex-end"}
      >
        {currentStepIndex > 0 && (
          <Button onClick={handlePrevious}>
            <FormattedMessage
              id="ui_component.common.label.back"
              defaultMessage="Back"
            />
          </Button>
        )}
        <PrimaryButton
          disabled={
            isUploadingLogo ||
            isGenerating ||
            (isLastStep && !selectedLayout) ||
            step0DisableNextButton
          }
          onClick={handleNext}
          loading={isGenerating}
        >
          {" "}
          {isLastStep ? (
            <FormattedMessage
              id="design_my_screen_modal.generate"
              defaultMessage="Generate"
            />
          ) : (
            <FormattedMessage
              id="design_my_screen_modal.next"
              defaultMessage="Next"
            />
          )}
        </PrimaryButton>
      </Footer>
    </DesignMyScreenContainer>
  );
};

export const DesignYourContent = withData(DesignYourContentComponent);
